// --- Core ---
import {ReactElement} from "react";
import {FieldRenderProps} from "react-final-form";
import {map} from "underscore";
// --- MUI ---
import {Autocomplete} from "@mui/material";
// --- SaniQ ---
import {TextField} from "helpers/forms/fields/TextField";

interface MultiSelectFieldProps<T> extends FieldRenderProps<T[]> {
  /**
   * Specifies if creation is allowed
   * @default false
   */
  allowCreation?: boolean;
  //Specifies if the input is in the options
  inputInOptions: boolean;
  //The label for the multi-select field
  label: ReactElement;
  //The options for the multi-select field
  options: T[];
  /**
   * Specifies if the multi-select field is required
   * @default false
   */
  required?: boolean;
}

/**
 * Represents a component for a multi-select field.
 */
export const MultiSelectField = <T, >(
  {
    input,
    inputInOptions,
    options,
    label,
    meta,
    allowCreation = false,
    required = false,
    ...custom
  }: MultiSelectFieldProps<T>
) => (
  <Autocomplete<T, true>
    multiple fullWidth
    freeSolo filterSelectedOptions
    autoHighlight
    options={inputInOptions ? [...options, ...input.value] : options}
    {...input}
    value={input.value || []}
    getOptionLabel={(option) => option.name}
    onChange={
      (_event, values, reason) => {
        let actualValues = values;

        if (allowCreation && reason === "createOption") {
          actualValues = map(values, (value) => {
            if (typeof value === "string") {
              return {name: value, id: null};
            }

            return value;
          });
        }
        input.onChange(actualValues);
      }
    }
    renderInput={
      (params) => (
        //NOTE(Keune): Required cannot be passed to the TextField directly since in a MultiSelect context the input value
        // is always empty. This triggers the html5 native validation and disables submitting of the form.
        <TextField
          meta={meta}
          {...params}
          inputProps={{...params.inputProps, maxLength: 25}}
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          label={<span>{label}{required ? " *" : ""}</span>}
        />
      )
    }
    {...custom}
    slotProps={{chip: {size: "small"}}}
  />
);
